'use client';

import type { FC } from 'react';
import { Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, makeFragmentData } from '@packages/gql/generated/shopping';
import { InspiringSearchPreviewFragmentFragmentDoc } from '@packages/gql/generated/shopping/InspiringSearchPreviewFragmentFragmentDoc';
import { SearchProductCardResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardResultFragmentFragmentDoc';
import {
  Box,
  Grid,
  Typography,
  ShimmeringBlurOverlay,
  useRouterTransition,
  ErrorBoundary,
} from '@packages/shared';

import { InspiringSearchMoreCard } from '../InspiringSearchMoreCard';
import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';
import { getGridColumns } from '../ProductGrid/getGridColumns';
import { getCardSize } from '../ProductGrid/getCardSize';
import { SearchProductCard } from '../SearchProductCard';
import { SearchFeedback } from '../SearchFeedback';
import { ColorSelectFailedNotification } from '../ColorSelectFailedNotification';
import { SearchKimRating } from '../SearchKimRating';

/* GraphQL */ `
  fragment InspiringSearchPreviewFragment on SuggestionPreviewSearchResult {
    suggestions {
      id
      items {
        styleId
        ...SearchProductCardFragment
      }
      query
      ...InspiringSearchMoreCardFragment
    }
    ...SearchKimRatingFragment
  }
`;

export type InspiredSearchPreviewProps = {
  maskedData: FragmentType<typeof InspiringSearchPreviewFragmentFragmentDoc>;
  referrer?: string;
};

const INSPIRING_SEARCH_DISPLAY_MODE = 'cards';

const messages = defineMessages({
  header: {
    id: 'search.inspiringSearch.previewPage.header',
    defaultMessage: 'Suchvorschläge für',
    description: 'Inspired search preview page header',
  },
});

/**
 * Preview page for AI-suggested search queries, listing multiple options to continue the user journey after the initial natural language query.
 * */
export const InspiringSearchPreview: FC<InspiredSearchPreviewProps> = ({
  maskedData,

  referrer,
}) => {
  const data = getFragmentData(InspiringSearchPreviewFragmentFragmentDoc, maskedData);

  const { formatMessage } = useIntl();

  const originalQuery = useOriginalSearchQuery();

  const { isTransitioning } = useRouterTransition();

  const columns = getGridColumns(INSPIRING_SEARCH_DISPLAY_MODE);

  return (
    <>
      <Box display="flex" flexDirection="column" marginX={2} position="relative">
        <ShimmeringBlurOverlay show={isTransitioning} />
        <ColorSelectFailedNotification />

        <Typography variant="body2" sx={{ alignSelf: 'center', marginTop: 2.5 }}>
          {formatMessage(messages.header)}
        </Typography>
        <Typography variant="body2" sx={{ alignSelf: 'center', marginBottom: 2 }}>
          „{originalQuery}“
        </Typography>
        {data.suggestions.map((suggestion) => (
          <Fragment key={suggestion.query}>
            <Typography variant="h4" sx={{ marginTop: 1, marginBottom: 1 }}>
              {suggestion.query}
            </Typography>

            <Grid
              container
              spacing={{ xs: 1.5, md: 2 }}
              columns={columns}
              component="ul"
              sx={{
                listStyle: 'none',
                paddingLeft: 0,
              }}
            >
              {suggestion.items.map((item) => (
                <Grid
                  item
                  component="li"
                  key={item.styleId}
                  {...getCardSize(columns, INSPIRING_SEARCH_DISPLAY_MODE, false)}
                >
                  <SearchProductCard
                    maskedItem={item}
                    maskedResult={makeFragmentData(
                      {
                        id: suggestion.id || '',
                        categories: [],
                        defaultDisplayMode: 'CARDS',
                        abTesting: null,
                      },
                      SearchProductCardResultFragmentFragmentDoc,
                    )}
                  />
                </Grid>
              ))}
              <Grid
                item
                component="li"
                {...getCardSize(columns, INSPIRING_SEARCH_DISPLAY_MODE, false)}
              >
                <InspiringSearchMoreCard maskedSuggestion={suggestion} />
              </Grid>
            </Grid>
          </Fragment>
        ))}

        <SearchFeedback />
      </Box>
      <ErrorBoundary>
        <SearchKimRating
          maskedData={data}
          searchPageType="SERP"
          templateParams={{ x17: referrer ?? 'x' }}
        />
      </ErrorBoundary>
    </>
  );
};
